import React, { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import api from '../utils/api';

const labelAnimal = (animal) => {
  if (!animal) return '';
  return `${animal.brinco || ''}${animal.nome ? ` - ${animal.nome}` : ''}`;
};

export default function AnimalSearchSelect({
  value,
  onChange,
  placeholder = 'Digite brinco, nome ou chip...',
  emptyLabel = 'Nenhum',
  situacao = 'ativo',
  sexo = '',
  limit = 1000,
  required = false,
  style,
}) {
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focado, setFocado] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDocMouseDown = (event) => {
      if (!boxRef.current?.contains(event.target)) setFocado(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!value) {
      setSelecionado(null);
      setTermo('');
      return;
    }
    if (selecionado && String(selecionado.id) === String(value)) return;

    let ativo = true;
    api.get(`/animais/${value}`)
      .then(r => {
        if (!ativo) return;
        setSelecionado(r.data);
        setTermo(labelAnimal(r.data));
      })
      .catch(() => {
        if (ativo) {
          setSelecionado(null);
          setTermo('');
        }
      });
    return () => { ativo = false; };
  }, [value, selecionado]);

  useEffect(() => {
    if (!focado || selecionado) {
      setResultados([]);
      setLoading(false);
      return;
    }

    let ativo = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const busca = termo.trim();
        const params = { limit };
        if (busca) params.busca = busca;
        if (situacao) params.situacao = situacao;
        if (sexo) params.sexo = sexo;
        const { data } = await api.get('/animais', { params });
        if (ativo) setResultados(data.data || data || []);
      } catch {
        if (ativo) setResultados([]);
      } finally {
        if (ativo) setLoading(false);
      }
    }, 250);

    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [termo, focado, selecionado, situacao, sexo, limit]);

  const limpar = () => {
    setSelecionado(null);
    setTermo('');
    setResultados([]);
    onChange('');
  };

  const selecionar = (animal) => {
    setSelecionado(animal);
    setTermo(labelAnimal(animal));
    setResultados([]);
    setFocado(false);
    onChange(String(animal.id), animal);
  };

  return (
    <div className="animal-search-select" style={style} ref={boxRef}>
      <div className="animal-search-input-wrap">
        <Search size={16} className="animal-search-icon" />
        <input
          value={termo}
          onFocus={() => setFocado(true)}
          onChange={e => {
            setTermo(e.target.value);
            setSelecionado(null);
            onChange('');
          }}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
        />
        {(termo || value) && (
          <button type="button" className="animal-search-clear" onClick={limpar} title="Limpar animal">
            <X size={14} />
          </button>
        )}
      </div>

      {selecionado && (
        <div className="animal-search-selected">
          <span>Selecionado: <strong>{selecionado.brinco}</strong>{selecionado.nome ? ` - ${selecionado.nome}` : ''}</span>
        </div>
      )}

      {focado && !selecionado && (
        <div className="animal-search-results">
          {loading ? (
            <div className="animal-search-empty">Buscando...</div>
          ) : resultados.length === 0 ? (
            <div className="animal-search-empty">{termo.trim() ? 'Nenhum animal encontrado' : emptyLabel}</div>
          ) : resultados.map(animal => (
            <button key={animal.id} type="button" className="animal-search-option" onClick={() => selecionar(animal)}>
              <strong>{animal.brinco}</strong>{animal.nome ? ` - ${animal.nome}` : ''}
              <span>{[animal.categoria, animal.raca_nome, animal.lote_nome].filter(Boolean).join(' · ') || 'Sem detalhes'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
